import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Cpu, MessageSquare, BarChart3, ShieldCheck, Workflow, Sparkles } from 'lucide-react';
import Eyebrow from './Eyebrow';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

const capabilities = [
  { icon: Sparkles, title: 'AI Strategy & Roadmapping', desc: 'Identify high-ROI AI opportunities and build a phased adoption roadmap aligned to business goals.' },
  { icon: MessageSquare, title: 'LLM & Generative AI', desc: 'Custom GPT integrations, RAG pipelines, and AI assistants built on GPT-4, Claude, Gemini, and open-source LLMs.' },
  { icon: Workflow, title: 'Intelligent Automation', desc: 'Automate document processing, customer support, and repetitive workflows with AI agents.' },
  { icon: BarChart3, title: 'Predictive Analytics', desc: 'Forecast demand, detect churn, and surface business insights using ML models trained on your own data.' },
  { icon: Cpu, title: 'AI-Augmented Products', desc: 'Embed smart search, recommendations, personalization, and intelligent UX features directly into your product.' },
  { icon: ShieldCheck, title: 'Responsible AI & Governance', desc: 'Build trust with bias auditing, explainability frameworks, data privacy controls, and AI governance policies.' },
];

function CapabilityCard({ c, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const Icon = c.icon;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.06, duration: 0.55, ease }}
      whileHover={{ y: -3, backgroundColor: '#fff' }}
      className="p-7 cursor-default rounded-2xl"
      style={{
        border: '1px solid rgba(0,0,0,0.06)',
        transition: 'background-color 160ms ease, box-shadow 160ms ease',
      }}
    >
      <motion.div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
        style={{ background: 'rgba(99,91,255,0.1)' }}
        whileHover={{ scale: 1.1 }}
        transition={{ duration: 0.18, ease }}
      >
        <Icon size={19} style={{ color: '#635BFF' }} />
      </motion.div>
      <h3 className="font-semibold mb-2 text-[15px] tracking-tight" style={{ color: '#0A0527', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {c.title}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{c.desc}</p>
    </motion.div>
  );
}

export default function AIConsulting() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: '-80px' });

  return (
    <section id="ai" className="px-5 sm:px-8 md:px-12 lg:px-20 flex flex-col justify-center py-24 overflow-hidden section-gray" style={{ minHeight: '100dvh' }}>
      <div className="max-w-7xl mx-auto">

        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={headerInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, ease }}>
              <Eyebrow>AI Consulting</Eyebrow>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={headerInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.08, ease }}
              className="text-4xl md:text-5xl lg:text-[3.25rem] font-bold tracking-tight leading-[1.08]"
              style={{ color: '#0A0527', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.025em' }}
            >
              AI That Moves Business Forward
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.16, ease }}
            className="text-lg leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            We translate AI hype into production-grade systems. No pilots that never ship. Practical solutions that deliver measurable results within your existing operations.
          </motion.p>
        </div>

        {/* Grid — border lines create cells */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          style={{}}
        >
          {capabilities.map((c, i) => (
            <CapabilityCard key={c.title} c={c} index={i} />
          ))}
        </div>

        {/* CTA block */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="mt-12 rounded-[1.75rem] overflow-hidden"
        >
          {/* Outer shell */}
          <div
            className="p-[7px] rounded-[1.75rem]"
            style={{ background: 'linear-gradient(135deg, #0f0c29 0%, #5E0ED7 100%)' }}
          >
            <div
              className="rounded-[1.375rem] p-10 md:p-14 text-white text-center"
              style={{
                background: 'linear-gradient(135deg, #130e3a 0%, #4a0bac 100%)',
                boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.08)',
              }}
            >
              <p className="text-[11px] font-semibold tracking-[0.14em] uppercase mb-4 opacity-50">
                AI Adoption Starts Here
              </p>
              <h3
                className="text-3xl md:text-4xl font-bold tracking-tight mb-4"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.02em' }}
              >
                Ready to Build Your AI Advantage?
              </h3>
              <p className="text-white/60 text-base mb-8 max-w-lg mx-auto leading-relaxed">
                Book a free 45-minute AI strategy session. We'll map your highest-impact opportunities and outline a practical path to implementation.
              </p>
              {/* Button-in-button */}
              <motion.a
                href="#contact"
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.12 }}
                className="inline-flex items-center gap-2 bg-white font-semibold text-sm tracking-wide uppercase px-6 py-3.5 rounded-full"
                style={{ color: ACCENT }}
              >
                Book AI Strategy Session
                <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: `${ACCENT}12` }}>
                  <span style={{ fontSize: 11, color: ACCENT }}>↗</span>
                </div>
              </motion.a>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
